import { basemapOptions } from '../map/basemapOptions';

export interface Attribution {
  name: string;
  usage: string;
  licence: string;
  licenceUrl?: string;
  attribution: string;
  sourceUrl: string;
}

export interface SoftwareLicence {
  name: string;
  usage: string;
  licence: string;
  noticeUrl: string;
}

export const DATA_SOURCE_ATTRIBUTIONS: Attribution[] = [
  {
    name: 'OpenStreetMap',
    usage: 'Road network, stops, points of interest and Overpass queries',
    licence: 'Open Database Licence (ODbL) 1.0',
    licenceUrl: 'https://opendatacommons.org/licenses/odbl/1-0/',
    attribution: '© OpenStreetMap contributors',
    sourceUrl: 'https://www.openstreetmap.org/copyright',
  },
  {
    name: 'WorldPop',
    usage: 'Gridded population estimates',
    licence: 'Creative Commons Attribution 4.0 International (CC BY 4.0)',
    licenceUrl: 'https://creativecommons.org/licenses/by/4.0/',
    attribution: 'WorldPop (www.worldpop.org), University of Southampton',
    sourceUrl: 'https://www.worldpop.org',
  },
  {
    name: 'Australian Bureau of Statistics',
    usage: 'Australian census population and boundaries',
    licence: 'Creative Commons Attribution 4.0 International (CC BY 4.0)',
    licenceUrl: 'https://creativecommons.org/licenses/by/4.0/',
    attribution: 'Source: Australian Bureau of Statistics',
    sourceUrl: 'https://www.abs.gov.au',
  },
  {
    name: 'United States Census Bureau',
    usage: 'American Community Survey population and census geocoding',
    licence: 'Public domain',
    attribution: 'Source: U.S. Census Bureau. This product uses the Census Bureau Data API but is not endorsed or certified by the Census Bureau.',
    sourceUrl: 'https://www.census.gov/data/developers/about/terms-of-service.html',
  },
  {
    name: 'Bus Open Data Service (Department for Transport)',
    usage: 'Great Britain bus timetables',
    licence: 'Open Government Licence v3.0',
    licenceUrl: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attribution: 'Contains public sector information licensed under the Open Government Licence v3.0.',
    sourceUrl: 'https://www.bus-data.dft.gov.uk',
  },
  {
    name: 'National Records of Scotland',
    usage: 'Scotland census 2022 population',
    licence: 'Open Government Licence v3.0',
    licenceUrl: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attribution: 'Contains NRS data © Crown copyright and database right.',
    sourceUrl: 'https://www.scotlandscensus.gov.uk',
  },
  {
    name: 'Office for National Statistics (via Nomis)',
    usage: 'England and Wales census 2021 population and demographics',
    licence: 'Open Government Licence v3.0',
    licenceUrl: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attribution: 'Source: Office for National Statistics licensed under the Open Government Licence v.3.0',
    sourceUrl: 'https://www.nomisweb.co.uk',
  },
  {
    name: 'ONS Open Geography Portal',
    usage: 'England and Wales census geography (LSOA boundaries)',
    licence: 'Open Government Licence v3.0',
    licenceUrl: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attribution: 'Contains OS data © Crown copyright and database right',
    sourceUrl: 'https://geoportal.statistics.gov.uk',
  },
  {
    name: 'Scottish Government (maps.gov.scot)',
    usage: 'Scotland census geography (Data Zone boundaries)',
    licence: 'Open Government Licence v3.0',
    licenceUrl: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attribution: 'Contains public sector information licensed under the Open Government Licence v3.0',
    sourceUrl: 'https://maps.gov.scot',
  },
  {
    name: 'openrouteservice (HeiGIT)',
    usage: 'Isochrones',
    licence: 'openrouteservice terms of service',
    licenceUrl: 'https://openrouteservice.org/terms-of-service/',
    attribution: '© openrouteservice.org by HeiGIT | Map data © OpenStreetMap contributors',
    sourceUrl: 'https://openrouteservice.org',
  },
  {
    name: 'Google Maps Platform',
    usage: 'Address search, place names, transit travel times and map tiles',
    licence: 'Google Maps Platform Terms of Service',
    licenceUrl: 'https://cloud.google.com/maps-platform/terms',
    attribution: 'Google Maps',
    sourceUrl: 'https://mapsplatform.google.com',
  },
  {
    name: 'Mapbox',
    usage: 'Route geometry',
    licence: 'Mapbox terms of service',
    licenceUrl: 'https://www.mapbox.com/legal/tos',
    attribution: '© Mapbox © OpenStreetMap contributors',
    sourceUrl: 'https://www.mapbox.com/about/maps/',
  },
];

const BASEMAP_PROVIDERS: Record<keyof typeof basemapOptions, { name: string; sourceUrl: string }> = {
  standard: { name: 'OpenStreetMap standard tiles', sourceUrl: 'https://operations.osmfoundation.org/policies/tiles/' },
  aesthetic: { name: 'CARTO Voyager', sourceUrl: 'https://carto.com/attributions' },
  satellite: { name: 'Esri World Imagery', sourceUrl: 'https://www.esri.com/en-us/legal/terms/full-master-agreement' },
  transit: { name: 'MeMoMaps ÖPNVKarte', sourceUrl: 'https://memomaps.de' },
};

export const BASEMAP_ATTRIBUTIONS = (Object.keys(BASEMAP_PROVIDERS) as Array<keyof typeof basemapOptions>).map((key) => ({
  key,
  name: BASEMAP_PROVIDERS[key].name,
  attribution: basemapOptions[key].attribution,
  sourceUrl: BASEMAP_PROVIDERS[key].sourceUrl,
}));

export const SOFTWARE_LICENCES: SoftwareLicence[] = [
  {
    name: 'react-leaflet and Leaflet',
    usage: 'Interactive map rendering',
    licence: 'Hippocratic License 2.1 (react-leaflet) and BSD 2-Clause License (Leaflet)',
    noticeUrl: '/third-party-notices.txt',
  },
];
